// src/services/easyhubQueries.js
import {easyHubRequest} from './api';
import {getUserInfoFornecedor} from './GetUserInfoFornecedor';

/**
 * Carrega os parâmetros de autenticação (cnpj_empresa, cod_cli, loj_cli) do fornecedor logado.
 * @returns {Promise<{ cnpj_empresa: string, cod_cli: string, loj_cli: string }>}
 */
async function loadAuthParams() {
    return getUserInfoFornecedor();
}

/**
 * Helper genérico para requisições ao EasyHub.
 *
 * - Para GET: basta passar a URL completa (com query string) em `path` e `method = 'get'`.
 * - Para POST: passe `path` como o endpoint, e `body` com os campos específicos.
 *
 * Em caso de falha de rede ou erro do servidor, registra no console (incluindo `response.data` se disponível)
 * e lança um Error contendo a mensagem de retorno do backend ou um texto genérico.
 *
 * @param {string} path               Endpoint ou URL com query string (GET)
 * @param {object} [body={}]          Payload para POST
 * @param {'post'|'get'} [method='post']  Método HTTP
 * @returns {Promise<any>}            O conteúdo de `response.data`
 * @throws {Error}                    Com `response.data.Retorno` ou mensagem padrão
 */
async function request(path, body = {}, method = 'post') {
    try {
        let response;
        if (method.toLowerCase() === 'get') {
            // `easyHubRequest` retorna todo o objeto AxiosResponse
            response = await easyHubRequest(path, 1);
        } else {
            const auth = await loadAuthParams();
            const payload = {
                cnpj_empresa: auth.cnpj_empresa,
                ...body,
            };
            response = await easyHubRequest(path, 1, {}, payload, method);
        }
        // Retorna somente o .data para que todos os serviços já entreguem o payload correto
        return response.data;
    } catch (error) {
        // Log detalhado para facilitar debug
        console.error(
            `[EasyHub ${method.toUpperCase()} ${path}]`,
            error.response?.data || error.message,
        );
        // Mensagem do backend ou genérica
        const msg = error.response?.data?.Retorno || error.message || 'Erro desconhecido';
        throw new Error(msg);
    }
}

/**
 * Retorna as ordens de serviço geradas no app. LOCW023
 *
 * GET /RETOS?cod={Código do Fornecedor}&loja={Loja}&aut={CNPJ}
 *
 * @returns {Promise<object>} Exemplo de resposta:
 * {
 *   "Retorno": "OK",
 *   "CodFor": "18209965",
 *   "Nome": "BAMAQ SA …",
 *   "Ordens": [ * … * ]
 * }
 */
export const fetchOrdensServico = async (cdtini = '', cdtfim = '') => {
    const auth = await loadAuthParams();
    const qs = new URLSearchParams({
        cod: auth.cod_cli,
        loja: auth.loj_cli,
        aut: auth.cnpj_empresa,
        cdtini,
        cdtfim,
    }).toString();
    return request(`RETOS?${qs}`, {}, 'get');
};

/**
 * Envia os dados para gerar uma nova ordem de serviço. LOCW021
 *
 * POST /GERAOS
 *
 * @param {string} bem        Código do bem/equipamento (ex: "077LOC")
 * @param {string} servico    Código do serviço (ex: "001LOC")
 * @param {string} num_as     Número da AS vinculada (ex: "3000251001001D MG 01")
 * @param {string} hodometro  Hodômetro ou contador (ex: "35000")
 * @returns {Promise<object>} Exemplo de resposta:
 * {
 *   "Retorno": "OK",
 *   "001974"
 * }
 */
export const geraOrdemServico = (bem, servico, num_as, hodometro) =>
    request('GERAOS', {bem, servico, num_as, hodometro});

/**
 * Envia os insumos para uma ordem de serviço. LOCW022
 *
 * POST /GERAINSU
 *
 * @param {string} num_os       Número da OS enviado pelo APP (ex: "002228")
 * @param {Array<Object>} itens Array de itens detalhados, cada um contendo:
 *   - TipoInsumo, CodInsumo, Qtd, VlrUnit, VlrTotal,
 *     Destino, CodForn, LojForn, NomeForn,
 *     Anexos: [ { name, size, base64 }, … ]
 * @returns {Promise<object>}   { "Retorno": "OK", "NumOs": "002228" }
 */
export const geraInsumos = (num_os, itens) =>
    request('GERAINSU', {
        num_os,
        Itens: itens,
    });

/**
 * Altera os insumos de uma ordem de serviço existente. LOCW024
 *
 * POST /ALTERAOS
 *
 * @param {string} ordem        Número da OS (ex: "000279")
 * @param {Array<Object>} itens Array de itens contendo:
 *   - produto (string), qtd (string), valor_uni (string), seq_tar (string),
 *     Anexos: [ { name, size, base64 }, … ]
 * @returns {Promise<object>} Exemplo de resposta:
 * {
 *   "Retorno": "OK"
 * }
 */
export const alteraInsumosOS = (ordem, itens) => request('ALTERAOS', {Ordem: ordem, Itens: itens});

/**
 * Encerra uma ordem de serviço. LOCW025
 *
 * POST /ENCERRAOS
 *
 * @param {string} ordem Número da OS (ex: "002235")
 * @returns {Promise<object>} Exemplo de sucesso:
 * { "Retorno": "OK" }
 * Em caso de já encerrada:
 * { "Retorno": "ERRO-O.S já se encontra encerrada em 02/06/2025 às 10:35." }
 */
export const encerraOS = (ordem) => request('ENCERRAOS', {Ordem: ordem});

/**
 * Query 08: Retorna notas de retorno e seus respectivos títulos a pagar. LOCW001
 *
 * POST /APPQUERYRESULT
 *
 *  * Parâmetros obrigatórios:
 * - cod_query = "08"
 * - cod_for: Código do fornecedor logado
 * - loj_for: Loja do fornecedor logado
 * @returns {Promise<object>} Exemplo de resposta:
 * {
 *   "Retorno": "OK",
 *   "Dados": [
 *     {
 *       "NOTA": "MCD00Z",
 *       "OS": "002228",
 *       "PED_COMPRA": "TST161",
 *       "VLR_TOTAL": 200,
 *       "EMISS_NOTA": "20240918",
 *       "PARCELA": "2",
 *       "PREV_PAGTO": "20240919",
 *       "PAGO_EM": ""
 *     },
 *     …
 *   ]
 * }
 */
/**
+ * Query 08: Notas de retorno e títulos a pagar (por período).
+ * @param {string} cdtini - AAAAMMDD
+ * @param {string} cdtfim - AAAAMMDD
+ */
export const fetchNotasRetorno = async (cdtini = '', cdtfim = '') => {
    const auth = await loadAuthParams();
    return request('APPQUERYRESULT', {
        cod_query: '08',
        cod_for: auth.cod_cli,
        loj_for: auth.loj_cli,
        // Backend usa cdtini/cdtfim (vide SQL). Incluo dtini/dtfim também para compatibilidade.
        cdtini,
        cdtfim,
        dtini: cdtini,
        dtfim: cdtfim,
    });
};

/**
 * Query 09: Retorna tabela de preço de compras por fornecedor. LOCW001
 *
 * POST /APPQUERYRESULT
 *
 * @returns {Promise<object>} Exemplo de resposta:
 * {
 *   "Retorno": "OK",
 *   "Dados": [
 *     {
 *       "AIA_DATATE": "20491231",
 *       "AIB_CODFOR": "COM002",
 *       "AIB_LOJFOR": "01",
 *       "AIB_CODPRO": "COM000…",
 *       "B1_DESC": "PRODUTO PA COMPRAS 003-TABELA",
 *       "B1_UM": "UN",
 *       "AIB_PRCCOM": 3.5,
 *       "AIB_CODTAB": "002",
 *       "AIB_ITEM": "0001"
 *     },
 *     …
 *   ]
 * }
 */
export const fetchTabelaPrecoCompras = async () => {
    const auth = await loadAuthParams();
    return request('APPQUERYRESULT', {
        cod_query: '09',
        cod_for: auth.cod_cli,
        loj_for: auth.loj_cli,
    });
};

/**
 * Anexa arquivos a um pedido de compras no APP. LOCW027
 *
 * POST /ImgPC
 *
 * @param {string} pedido               Número do pedido de compras (ex: "TSUBA3")
 * @param {Array<{ name: string, Img: string }>} anexos
 *   Lista de anexos onde:
 *     - name: nome do arquivo (ex: "anexo_1.JPG")
 *     - Img: conteúdo em base64 (ou identificador do tipo, ex: "pdf")
 * @returns {Promise<object>}           Exemplo de resposta:
 * {
 *   "Retorno": "OK",
 *   "Pedido": "TSUBA3"
 * }
 */
export const anexarArquivosNfOs = (pedido, anexos) => request('ImgPC', {pedido, Anexos: anexos});

/**
 * Envia arquivos anexos da nota via OS (Query 08). LOCW028
 *
 * POST /AnxOs
 *
 * @param {string} ordem   Número da OS vinculada (ex: "002228")
 * @param {Array<{ name: string, Img: string }>} anexos Lista de arquivos base64
 * @returns {Promise<object>} Exemplo de resposta:
 * {
 *   "Retorno": "OK",
 *   "Ordem de Servico": "002228"
 * }
 */
export const enviarAnexoNotaPagamento = async (ordem, produto, seq_tar, anexos) => {
    const auth = await loadAuthParams();
    return request('AnxOs', {
        cnpj_empresa: auth.cnpj_empresa,
        ordem: ordem,
        produto, // código do insumo
        seq_tar, // sequência do item
        Anexos: anexos,
    });
};

/**
 * Exclui insumos de uma ordem de serviço (LOCW029).
 *
 * POST /DelInsu
 *
 * @param {string} ordem Número da Ordem de Serviço (ex: "002228")
 * @param {Array<{ produto: string, seq: string }>} itens Lista de insumos a excluir
 * @returns {Promise<object>} Exemplo de resposta:
 * {
 *   "Retorno": "OK"
 * }
 */
export const excluirInsumosOS = async (ordem, itens) => {
    const auth = await loadAuthParams();
    return request('DelInsu', {
        cnpj_empresa: auth.cnpj_empresa,
        ordem,
        Itens: itens,
    });
};

/**
 * Query 06: retorna lista de tipos de serviço.
 * POST /APPQUERYRESULT
 * Parâmetros obrigatórios:
 *   cod_query = "06"
 *   cod_for = código do fornecedor
 *   loj_for = loja do fornecedor
 */
export const fetchTiposServico = async () => {
    const auth = await loadAuthParams();
    const resp = await request('APPQUERYRESULT', {
        cod_query: '06',
        cod_for: auth.cod_cli,
        loj_for: auth.loj_cli,
    });

    if (resp.Retorno !== 'OK' || !Array.isArray(resp.Dados)) {
        throw new Error(resp.Retorno || 'Falha ao carregar tipos de serviço');
    }

    return resp.Dados.map((item) => ({
        code: item.T4_SERVICO, // antes: item.COD_SERV
        label: item.T4_NOME, // antes: item.DESCR_SERV
    }));
};

/**
 * Query 10: retorna a última AS do bem. LOCW010
 *
 * @param {string} bem Código do bem
 * @returns {Promise<string>} O número da AS (campo FQ4_AS)
 */
export const fetchUltimaAS = async (bem) => {
    const auth = await loadAuthParams();

    // 1) chama a query no servidor
    const respRaw = await request('APPQUERYRESULT', {
        cod_query: '10',
        cod_bem: bem,
        // se sua query 10 precisar de cod_for/loj_for, adicione aqui
        // cod_for: auth.cod_cli,
        // loj_for: auth.loj_cli,
    });

    // 2) se veio string, tenta extrair só o FQ4_AS via regex
    if (typeof respRaw === 'string') {
        // procura algo como: "FQ4_AS":"VALOR"
        const m = respRaw.match(/"FQ4_AS"\s*:\s*"([^"]*)"/);
        if (m && m[1]) {
            return m[1];
        }

        // se não achou, por via das dúvidas tenta um JSON.parse
        try {
            const obj = JSON.parse(respRaw);
            if (
                obj.Retorno === 'OK' &&
                Array.isArray(obj.Dados) &&
                obj.Dados.length > 0 &&
                typeof obj.Dados[0].FQ4_AS === 'string'
            ) {
                return obj.Dados[0].FQ4_AS;
            }
            throw new Error(obj.Retorno || 'Nenhuma AS encontrada');
        } catch (e) {
            throw new Error('Resposta do servidor malformada: não foi possível obter AS');
        }
    }

    // 3) se já veio objeto
    if (
        respRaw &&
        respRaw.Retorno === 'OK' &&
        Array.isArray(respRaw.Dados) &&
        respRaw.Dados.length > 0
    ) {
        return respRaw.Dados[0].FQ4_AS;
    }

    throw new Error((respRaw && respRaw.Retorno) || 'Nenhuma AS encontrada');
};
